import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, MessageCircle, MapPin, Phone, Facebook } from "lucide-react";

const SOCIALS = [
  {
    href: "https://www.instagram.com/emgroup.digital?igsh=MTFlY2NuZmMwcmdhMg==",
    label: "@emgroup.digital",
    Icon: Instagram,
    testid: "footer-social-instagram",
    color: "#E52431",
  },
  {
    href: "https://www.facebook.com/share/18b4ff5NHc/?mibextid=wwXIfr",
    label: "EM Group",
    Icon: Facebook,
    testid: "footer-social-facebook",
    color: "#1F2C6E",
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/5 bg-[#0A0A0A] pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src="/brand/logo-em-group.png"
                alt="EM Group"
                className="h-10 w-10 object-contain"
                style={{ maxWidth: 40, maxHeight: 40 }}
              />
              <span className="font-outfit tracking-tight text-lg text-white">
                EM <span className="text-white/50">Group</span>
              </span>
            </div>
            <p className="mt-6 text-white/55 font-light max-w-md leading-relaxed">
              Stratégie, branding et développement digital pour des marques
              ambitieuses.
            </p>

            {/* Social icons */}
            <div className="mt-7 flex items-center gap-3">
              {SOCIALS.map((s) => {
                const I = s.Icon;
                return (
                  <a
                    key={s.testid}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    title={s.label}
                    data-testid={s.testid}
                    className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] transition-all duration-300 hover:scale-105 hover:border-white/30"
                    style={{
                      boxShadow: `0 0 0 0 ${s.color}55`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `0 0 25px ${s.color}66`;
                      e.currentTarget.style.borderColor = `${s.color}88`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = `0 0 0 0 ${s.color}55`;
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                    }}
                  >
                    <I
                      size={17}
                      strokeWidth={1.6}
                      style={{ color: s.color }}
                      className="transition-transform group-hover:scale-110"
                    />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              Nos services
            </h4>
            <ul className="space-y-3 text-white/75 font-light">
              <li>
                <a
                  data-testid="footer-link-branding"
                  href="/#branding"
                  className="hover:text-white transition-colors"
                >
                  Branding &amp; identité visuelle
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-link-sites-web"
                  href="/#sites-web"
                  className="hover:text-white transition-colors"
                >
                  Création de site web
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-link-accompagnement"
                  href="/#accompagnement"
                  className="hover:text-white transition-colors"
                >
                  Accompagnement complet
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-link-tarifs"
                  href="/#tarifs"
                  className="hover:text-white transition-colors"
                >
                  Tarifs
                </a>
              </li>
              <li>
                <Link
                  to="/diagnostic-digital-gratuit"
                  data-testid="footer-link-diagnostic"
                  className="hover:text-white transition-colors inline-flex items-center gap-2"
                >
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E52431] animate-pulse" />
                  Diagnostic digital gratuit
                </Link>
              </li>
              <li>
                <Link
                  to="/creation-logo-paris"
                  data-testid="footer-link-logo-paris"
                  className="hover:text-white transition-colors"
                >
                  Création de logo à Paris
                </Link>
              </li>
              <li>
                <Link
                  to="/creation-logo-la-reunion"
                  data-testid="footer-link-logo-reunion"
                  className="hover:text-white transition-colors"
                >
                  Création de logo à La Réunion
                </Link>
              </li>
              <li>
                <Link
                  to="/creation-logo-ile-maurice"
                  data-testid="footer-link-logo-maurice"
                  className="hover:text-white transition-colors"
                >
                  Création de logo à l&rsquo;île Maurice
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              Contact
            </h4>
            <ul className="space-y-3 text-white/75">
              <li>
                <a
                  data-testid="footer-email"
                  href="mailto:contact@emgroupdigital.fr"
                  className="inline-flex items-center gap-3 hover:text-white transition-colors break-all"
                >
                  <Mail size={16} className="text-[#E52431] flex-shrink-0" />
                  <span className="font-light">contact@emgroupdigital.fr</span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone"
                  href="tel:+262262667970"
                  className="inline-flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Phone size={16} className="text-[#1F2C6E] brightness-150 flex-shrink-0" />
                  <span className="font-light">
                    <span className="text-white/50">Appelez-nous&nbsp;·&nbsp;</span>+262 262 66 79 70
                  </span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href="https://wa.me/262693482110"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-3 hover:text-white transition-colors"
                >
                  <MessageCircle size={16} className="text-[#E52431] flex-shrink-0" />
                  <span className="font-light">
                    <span className="text-white/50">WhatsApp&nbsp;·&nbsp;</span>+262 693 48 21 10
                  </span>
                </a>
              </li>
            </ul>
          </div>

          {/* Adresse */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              Adresse
            </h4>
            <div className="flex items-start gap-3 text-white/75">
              <MapPin size={16} className="text-[#E52431] mt-1 flex-shrink-0" />
              <span className="font-light leading-relaxed">
                60 rue François 1<sup>er</sup>
                <br />
                75008 Paris
                <br />
                <span className="text-white/50 text-sm">
                  Maurice · Réunion · France
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div>© {year} EM Group — Tous droits réservés.</div>
          <div className="flex gap-6">
            <Link to="/mentions-legales" data-testid="footer-mentions" className="hover:text-white/70 transition-colors">
              Mentions légales
            </Link>
            <Link to="/confidentialite" data-testid="footer-privacy" className="hover:text-white/70 transition-colors">
              Confidentialité
            </Link>
            <Link to="/cgv" data-testid="footer-cgv" className="hover:text-white/70 transition-colors">
              CGV
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
